/**
 * AdminBar — bara fixă sus, vizibilă DOAR pentru admini.
 *
 * Conține:
 *  - Indicator de rol (Admin / Super admin)
 *  - Buton Moderare (cu contor pending)
 *  - Buton Users (lista userilor)
 *  - Buton Delogare
 *  - Toast-uri inline (dreapta, în bară) — NU ca overlay pe conținut
 *
 * Bara e `fixed top-0` și are 56px înălțime. Conținutul paginii trebuie
 * să aibă `padding-top: 56px` (sau să folosească spacer-ul de mai jos).
 */

import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import { useAdminAuth } from '@/hooks/useAdminAuth';
import { countPendingPhotos } from '@/lib/admin';
import { useToastQueue } from '@/lib/toast';
import clsx from 'clsx';

interface AdminBarProps {
  /** Cream spatiu sub bara fixa cand AdminBar e afisata. */
  reserveSpace?: boolean;
}

const TOAST_STYLES = {
  success: 'bg-success/95 text-bg-alt border-success',
  error: 'bg-error/95 text-bg-alt border-error',
  info: 'bg-bg text-fg border-border',
} as const;

const TOAST_ICON = {
  success: '✓',
  error: '✕',
  info: 'ⓘ',
} as const;

export function AdminBar({ reserveSpace = true }: AdminBarProps) {
  const { role, logout } = useAdminAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toasts, dismiss } = useToastQueue();

  const { data: pendingCount = 0 } = useQuery({
    queryKey: ['pending-count'],
    queryFn: countPendingPhotos,
    enabled: role !== null,
    refetchInterval: 10_000,
  });

  if (role === null) return null;

  const onLogout = async () => {
    await logout();
    navigate('/');
  };

  // Link-uri active
  const isModerate = location.pathname.startsWith('/admin/moderate');
  const isUsers = location.pathname.startsWith('/admin/users');

  return (
    <>
      <motion.div
        initial={{ y: -56, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -56, opacity: 0 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
        className="fixed left-0 right-0 top-0 z-modal flex h-14 items-center border-b border-border bg-fg text-bg shadow-md"
        role="banner"
        data-admin-bar="top"
      >
        <div className="container-page flex w-full items-center justify-between gap-md">
          {/* STÂNGA: titlu + rol */}
          <div className="flex items-center gap-md">
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-bg/60">
              Admin
            </p>
            <p className="font-serif text-sm italic text-bg/90">
              {role === 'super_admin' ? 'Super admin' : 'Admin'}
            </p>
          </div>

          {/* MIJLOC: toast-uri inline (notification slot) */}
          <div className="flex flex-1 items-center justify-center gap-sm overflow-hidden px-md">
            <AnimatePresence initial={false} mode="popLayout">
              {toasts.map((t) => (
                <motion.div
                  key={t.id}
                  layout
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.22, ease: 'easeOut' }}
                  onClick={() => dismiss(t.id)}
                  className={clsx(
                    'flex cursor-pointer items-center gap-sm rounded-md border px-md py-xs text-sm font-medium shadow-sm',
                    TOAST_STYLES[t.type],
                  )}
                  role="status"
                  title="Click pentru a închide"
                >
                  <span className="text-base font-bold leading-none">
                    {TOAST_ICON[t.type]}
                  </span>
                  <span className="truncate">{t.text}</span>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>

          {/* DREAPTA: acțiuni */}
          <div className="flex items-center gap-sm">
            <button
              type="button"
              onClick={() => navigate('/admin/moderate')}
              className={clsx(
                'relative flex items-center gap-sm rounded-md px-md py-xs font-sans text-sm transition-all',
                isModerate
                  ? 'bg-accent text-bg-alt'
                  : 'bg-bg text-fg hover:bg-bg-alt active:scale-[0.98]',
              )}
            >
              <span>Moderare</span>
              <span
                className={clsx(
                  'inline-flex min-w-[20px] items-center justify-center rounded-full px-1.5 text-xs font-bold',
                  pendingCount > 0
                    ? isModerate
                      ? 'bg-bg text-accent'
                      : 'bg-accent text-bg-alt animate-pulse'
                    : 'bg-bg-alt text-fg-soft',
                )}
              >
                {pendingCount}
              </span>
            </button>

            <button
              type="button"
              onClick={() => navigate('/admin/users')}
              className={clsx(
                'flex items-center gap-sm rounded-md px-md py-xs font-sans text-sm transition-all',
                isUsers
                  ? 'bg-accent text-bg-alt'
                  : 'bg-bg text-fg hover:bg-bg-alt active:scale-[0.98]',
              )}
            >
              <span>Users</span>
            </button>

            <button
              type="button"
              onClick={onLogout}
              className="rounded-md px-md py-xs font-sans text-sm text-bg/70 transition-all hover:text-bg"
              title="Delogare admin"
            >
              Delogare
            </button>
          </div>
        </div>
      </motion.div>
      {reserveSpace && <div className="h-14" aria-hidden="true" />}
    </>
  );
}